<template>
  <aside
    class="h-screen flex flex-col transition-all duration-300 ease-in-out relative z-50 select-none border-r border-slate-200/80 bg-white text-slate-600 shadow-sm"
    :class="[isCollapsed ? 'w-20' : 'w-64']"
  >
    <!-- Bouton Toggle Rétractable -->
    <button 
      @click="isCollapsed = !isCollapsed"
      class="absolute -right-3 top-7 w-6 h-6 bg-white rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-all shadow-sm z-[60]"
    >
      <svg 
        class="w-3.5 h-3.5 transition-transform duration-300" 
        :class="{ 'rotate-180': isCollapsed }"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
      >
        <path d="m15 18-6-6 6-6"/>
      </svg>
    </button>

    <!-- Header / Logo Medialink Solutions -->
    <div class="h-20 flex items-center px-5 mb-2">
      <div class="flex items-center gap-3 overflow-hidden">
        <div class="min-w-[36px] h-[36px] flex items-center justify-center bg-blue-600 rounded-xl text-white shadow-md shadow-blue-500/20">
          <img 
            src="../assets/vague_bleue_petite.png" 
            class="h-5 w-5 object-contain brightness-0 invert" 
            alt="Vague Medialink" 
          />
        </div>
        <div v-if="!isCollapsed" class="overflow-hidden">
          <span class="font-black text-slate-900 tracking-tight text-sm block leading-tight">Medialink</span>
          <span class="text-[10px] font-bold text-blue-600 uppercase tracking-widest block">Solutions</span>
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 px-3 space-y-6 overflow-y-auto scrollbar-none py-2">
      
      <!-- Section Pilotage -->
      <div class="space-y-1">
        <h3 v-if="!isCollapsed" class="px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
          Pilotage Officine
        </h3>
        <div class="space-y-1">
          <RouterLink
            v-for="item in infrastructureMenu"
            :key="item.label"
            :to="item.to"
            class="flex items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all relative text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 group"
            :active-class="activeClass"
          >
            <div class="w-5 h-5 flex items-center justify-center text-slate-400 group-hover:text-blue-600 transition-colors" v-html="item.icon"></div>
            <span v-if="!isCollapsed" class="whitespace-nowrap">{{ item.label }}</span>
            
            <div v-if="isCollapsed" class="absolute left-16 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-all whitespace-nowrap z-50 shadow-xl border border-slate-800">
              {{ item.label }}
            </div>
          </RouterLink>
        </div>
      </div>

      <!-- Section Administration -->
      <div class="space-y-1">
        <h3 v-if="!isCollapsed" class="px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">
          Administration
        </h3>
        <div class="space-y-1">
          <RouterLink
            v-for="item in adminMenu"
            :key="item.label"
            :to="item.to"
            class="flex items-center gap-3.5 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all relative text-slate-600 hover:bg-slate-100/70 hover:text-slate-900 group"
            :active-class="activeClass"
          >
            <div class="w-5 h-5 flex items-center justify-center text-slate-400 group-hover:text-blue-600 transition-colors" v-html="item.icon"></div>
            <span v-if="!isCollapsed" class="whitespace-nowrap">{{ item.label }}</span>
            
            <div v-if="isCollapsed" class="absolute left-16 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-all whitespace-nowrap z-50 shadow-xl border border-slate-800">
              {{ item.label }}
            </div>
          </RouterLink>
        </div>
      </div>
    </nav>

    <!-- Profil Utilisateur en bas -->
    <div class="p-3 border-t border-slate-100 bg-slate-50/50">
      <div class="flex items-center justify-between p-2 rounded-xl hover:bg-white transition-all shadow-sm">
        <div class="flex items-center gap-3 overflow-hidden">
          <img
            src="../assets/vague_bleue_petite.png"
            class="w-9 h-9 rounded-full object-cover border border-slate-200 p-0.5 bg-white shrink-0 shadow-sm"
            alt="Avatar"
          />
          <div v-if="!isCollapsed" class="overflow-hidden">
            <p class="text-xs font-bold text-slate-800 truncate leading-tight">{{ user.prenom }} {{ user.nom }}</p>
            <p class="text-[10px] text-blue-600 font-semibold truncate mt-0.5">{{ user.role || 'Superviseur' }}</p>
          </div>
        </div>
        <button
          v-if="!isCollapsed"
          @click="logout"
          title="Déconnexion"
          class="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>
          </svg>
        </button>
      </div>

      <div v-if="!isCollapsed" class="mt-3 text-[9px] text-center text-slate-400 font-medium tracking-wider uppercase">
        © Medialink Solutions 2026
      </div>
    </div>
  </aside>
</template>

<script>
import { RouterLink } from "vue-router";

const ICONS = {
  dashboard: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>`,
  kiosk: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>`,
  screen: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
  media: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11"/><rect width="14" height="12" x="2" y="6" rx="2"/></svg>`,
  client: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  users: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 14c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2"/></svg>`,
  tools: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>`,
  ticket: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9V5.2a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2V9a2 2 0 0 0 0 4v3.8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V13a2 2 0 0 0 0-4Z"/><path d="M12 3v18"/><path d="m9 15 3-3 3 3"/><path d="m9 9 3 3 3-3"/></svg>`,
  familypack: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 16l3-3 3 3 5-5"/><circle cx="7" cy="16" r="1"/><circle cx="10" cy="13" r="1"/><circle cx="13" cy="16" r="1"/><circle cx="18" cy="11" r="1"/></svg>`,
  developer: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 9-4 3 4 3"/><path d="m16 9 4 3-4 3"/><path d="m14 5-4 14"/></svg>`,
};

export default {
  components: { RouterLink },
  data() {
    return {
      isCollapsed: false,
      user: { prenom: 'Support', nom: 'Technique', role: 'Administrateur' },
      infrastructureMenu: [
        { label: "Dashboard", to: "/", icon: ICONS.dashboard },
        { label: "Bornes Kiosk", to: "/kiosk", icon: ICONS.kiosk },
        { label: "Écrans d'appel", to: "/screen", icon: ICONS.screen },
        { label: "Médias & Pubs", to: "/media-library", icon: ICONS.media },
      ],
      adminMenu: [
        { label: "Pharmacies / Clients", to: "/client", icon: ICONS.client },
        { label: "Guichets & Caisses", to: "/Counter-User", icon: ICONS.users },
        { label: "Support Technique", to: "/maintenance", icon: ICONS.tools },
        { label: "Tickets en direct", to: "/ticket", icon: ICONS.ticket },
        { label: "Rapports & Stats", to: "/family-pack-reports", icon: ICONS.familypack },
        
      ],
      // Style actif épuré et lumineux avec le bleu électrique de Medialink Solutions
      activeClass: "!bg-blue-50 !text-blue-600 font-semibold shadow-sm border border-blue-100"
    };
  },
  methods: {
    logout() {
      console.log("Déconnexion...");
    }
  }
};
</script>

<style scoped>
.scrollbar-none::-webkit-scrollbar { display: none; }
.scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }
</style>